// Full implementation migrated from application/auth/auth-service
export interface User {
  id: string;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  employee_id?: string;
  job_title?: string;
  department?: string;
  manager_id?: string;
  clearance_level?: 'unclassified' | 'confidential' | 'secret' | 'top_secret';
  need_to_know_compartments?: string[];
  compliance_certifications?: string[];
  compliance_frameworks?: string[];
  employment_start_date?: string;
  last_training_date?: string;
  typical_access_hours?: number[][];
  status: 'active' | 'inactive' | 'suspended';
  created_at: string;
  updated_at: string;
  last_login_at?: string;
  mfa_enabled?: boolean;
  mfa_methods?: string[];
}
export interface AuthTokens { access_token: string; refresh_token: string; }
export interface LoginRequest {
  username?: string;  // Optional for passkey auth
  password?: string;  // Optional for MFA/passkey auth
  mfa_token?: string; // For MFA authentication
  passkey_credential?: Record<string, unknown>; // For WebAuthn passkey authentication
  auth_mode: 'password' | 'mfa' | 'passkey'; // Tri-modal authentication
}
export interface RegistrationRequest { username: string; email: string; password: string; first_name: string; last_name: string; department?: string; employee_id?: string; manager_id?: string; roles?: string[]; }
export interface MFAMethod { id: string; type: 'totp' | 'sms' | 'email' | 'passkey' | 'biometric'; name: string; isEnabled: boolean; }
export interface MFAVerificationRequest { user_id: string; mfa_token: string; method: string; }
export interface AuthResponse { success: boolean; user?: User; tokens?: AuthTokens; requiresMfa?: boolean; mfaMethods?: MFAMethod[]; error?: string; riskScore?: number; requiresReauthentication?: boolean; accountLocked?: boolean; passwordExpired?: boolean; }
export interface RefreshTokenRequest { refresh_token: string; }
export interface RefreshTokenResponse { success: boolean; tokens?: AuthTokens; error?: string; }
export interface SecurityRiskAssessment { riskScore: number; riskLevel: 'low' | 'medium' | 'high'; deviceFingerprint: string; isKnownDevice: boolean; isKnownLocation: boolean; recommendations: string[]; }
export interface PasswordRecoveryRequest { email: string; }
export interface PasswordRecoveryVerifyRequest { email: string; token: string; }
export interface PasswordRecoveryResetRequest { email: string; token: string; newPassword: string; }
export interface ReauthenticationCheckRequest { operation: string; password: string; }
export interface ReauthenticationCheckResponse { requiresReauthentication: boolean; operation: string; riskScore: number; }

// =============================================================================
// CREDENTIAL AUTHENTICATION INTERFACES
// =============================================================================

export interface CredentialOptions {
  challenge: string;
  rp: {
    name: string;
    id: string;
  };
  user: {
    id: string;
    name: string;
    displayName: string;
  };
  pubKeyCredParams: Array<{
    type: "public-key";
    alg: number;
  }>;
  timeout: number;
  attestation: AttestationConveyancePreference;
  authenticatorSelection: {
    authenticatorAttachment: AuthenticatorAttachment;
    userVerification: UserVerificationRequirement;
  };
}

export interface CredentialResponse {
  id: string;
  rawId: ArrayBuffer;
  response: {
    clientDataJSON: ArrayBuffer;
    attestationObject: ArrayBuffer;
  };
  type: string;
}

export interface AuthenticationOptions {
  challenge: string;
  rpId: string;
  allowCredentials: Array<{
    type: "public-key";
    id: string;
    transports?: AuthenticatorTransport[];
  }>;
  userVerification: UserVerificationRequirement;
  timeout: number;
}

export interface AuthenticationResponse {
  id: string;
  rawId: ArrayBuffer;
  response: {
    clientDataJSON: ArrayBuffer;
    authenticatorData: ArrayBuffer;
    signature: ArrayBuffer;
    userHandle: ArrayBuffer;
  };
  type: string;
}

export interface CredentialInfo {
  id: string;
  name: string;
  created_at: string;
  last_used_at?: string;
}

class AuthService {
  private baseUrl = process.env.NEXT_PUBLIC_API_URL || '/api/v1';
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  constructor() {
    if (typeof window !== 'undefined') {
      this.accessToken = localStorage.getItem('access_token');
      this.refreshToken = localStorage.getItem('refresh_token');
    }
  }

  // =============================================================================
  // CREDENTIAL AUTHENTICATION METHODS
  // =============================================================================

  /**
   * Check if credential authentication is supported in the current browser
   */
  isCredentialSupported(): boolean {
    return typeof window !== 'undefined' &&
      'PublicKeyCredential' in window &&
      'credentials' in navigator;
  }

  /**
   * Start credential registration process
   */
  async startCredentialRegistration(username: string, displayName: string): Promise<CredentialOptions> {
    const response = await this.request<CredentialOptions>('/auth/passkey/register/start', {
      method: 'POST',
      body: JSON.stringify({ username, display_name: displayName }),
    });

    return response;
  }

  /**
   * Complete credential registration process
   */
  async finishCredentialRegistration(credential: PublicKeyCredential): Promise<{ success: boolean; message: string }> {
    const attestationResponse = credential.response as AuthenticatorAttestationResponse;

    const response = await this.request<{ success: boolean; message: string }>('/auth/passkey/register/finish', {
      method: 'POST',
      body: JSON.stringify({
        credential: {
          id: credential.id,
          rawId: this.arrayBufferToBase64url(credential.rawId),
          response: {
            clientDataJSON: this.arrayBufferToBase64url(attestationResponse.clientDataJSON),
            attestationObject: this.arrayBufferToBase64url(attestationResponse.attestationObject),
          },
          type: credential.type,
        },
      }),
    });

    return response;
  }

  /**
   * Start credential authentication process
   */
  async startCredentialAuthentication(username: string): Promise<AuthenticationOptions> {
    const response = await this.request<AuthenticationOptions>('/auth/passkey/authenticate/start', {
      method: 'POST',
      body: JSON.stringify({ username }),
    });

    return response;
  }

  /**
   * Complete credential authentication process
   */
  async finishCredentialAuthentication(credential: PublicKeyCredential): Promise<{ success: boolean; token: string; user: any }> {
    const assertionResponse = credential.response as AuthenticatorAssertionResponse;

    const response = await this.request<{ success: boolean; token: string; user: any }>('/auth/passkey/authenticate/finish', {
      method: 'POST',
      body: JSON.stringify({
        credential: {
          id: credential.id,
          rawId: this.arrayBufferToBase64url(credential.rawId),
          response: {
            clientDataJSON: this.arrayBufferToBase64url(assertionResponse.clientDataJSON),
            authenticatorData: this.arrayBufferToBase64url(assertionResponse.authenticatorData),
            signature: this.arrayBufferToBase64url(assertionResponse.signature),
            userHandle: assertionResponse.userHandle ? this.arrayBufferToBase64url(assertionResponse.userHandle) : null,
          },
          type: credential.type,
        },
      }),
    });

    return response;
  }

  /**
   * Get user's registered credentials
   */
  async getCredentials(): Promise<CredentialInfo[]> {
    const response = await this.request<{ credentials: CredentialInfo[] }>('/auth/passkey/list', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${this.getAccessToken()}`,
      },
    });

    return response.credentials || [];
  }

  /**
   * Delete a specific credential
   */
  async deleteCredential(credentialId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.request<{ success: boolean; message: string }>('/auth/passkey/delete', {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${this.getAccessToken()}`,
      },
      body: JSON.stringify({ credential_id: credentialId }),
    });

    return response;
  }

  /**
   * Create credential using browser API
   */
  async createCredential(options: CredentialOptions): Promise<PublicKeyCredential> {
    if (!this.isCredentialSupported()) {
      throw new Error('Credential authentication is not supported in this browser');
    }

    const publicKey = {
      ...options,
      challenge: this.base64urlToArrayBuffer(options.challenge),
      user: {
        ...options.user,
        id: this.base64urlToArrayBuffer(options.user.id),
      },
    };

    const credential = await navigator.credentials.create({
      publicKey,
    }) as PublicKeyCredential;

    if (!credential) {
      throw new Error('Credential creation failed');
    }

    return credential;
  }

  /**
   * Get credential using browser API
   */
  async getCredential(options: AuthenticationOptions): Promise<PublicKeyCredential> {
    if (!this.isCredentialSupported()) {
      throw new Error('Credential authentication is not supported in this browser');
    }

    const publicKey = {
      ...options,
      challenge: this.base64urlToArrayBuffer(options.challenge),
      allowCredentials: options.allowCredentials.map(cred => ({
        ...cred,
        id: this.base64urlToArrayBuffer(cred.id),
      })),
    };

    const credential = await navigator.credentials.get({
      publicKey,
    }) as PublicKeyCredential;

    if (!credential) {
      throw new Error('Credential retrieval failed');
    }

    return credential;
  }

  /**
   * Complete credential registration flow
   */
  async registerCredential(username: string, displayName: string): Promise<{ success: boolean; message: string }> {
    try {
      // Step 1: Start registration
      const options = await this.startCredentialRegistration(username, displayName);

      // Step 2: Create credential using browser API
      const credential = await this.createCredential(options);

      // Step 3: Complete registration
      const result = await this.finishCredentialRegistration(credential);

      return result;
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Credential registration failed'
      };
    }
  }

  /**
   * Complete credential authentication flow
   */
  async authenticateWithCredential(username: string): Promise<AuthResponse> {
    try {
      // Step 1: Start authentication
      const options = await this.startCredentialAuthentication(username);

      // Step 2: Get credential using browser API
      const credential = await this.getCredential(options);

      // Step 3: Complete authentication
      const result = await this.finishCredentialAuthentication(credential);

      if (result.success) {
        // Set tokens and return success response
        this.setTokens({ access_token: result.token, refresh_token: result.token });
        return {
          success: true,
          user: result.user,
          tokens: { access_token: result.token, refresh_token: result.token }
        };
      } else {
        return { success: false, error: 'Credential authentication failed' };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Credential authentication failed'
      };
    }
  }

  // =============================================================================
  // UTILITY METHODS FOR CREDENTIAL AUTHENTICATION
  // =============================================================================

  /**
   * Convert ArrayBuffer to base64url string
   */
  private arrayBufferToBase64url(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      // Default to 0 to satisfy noUncheckedIndexedAccess safety
      binary += String.fromCharCode(bytes[i] ?? 0);
    }
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Convert base64url string to ArrayBuffer
   */
  private base64urlToArrayBuffer(base64url: string): ArrayBuffer {
    const base64 = base64url
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const padding = '='.repeat((4 - base64.length % 4) % 4);
    const base64Padded = base64 + padding;

    const binaryString = atob(base64Padded);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes.buffer;
  }

  // =============================================================================
  // EXISTING AUTHENTICATION METHODS
  // =============================================================================

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(options.headers as Record<string, string>) };
    if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;

    console.log('Making request to:', url, 'with options:', { ...options, body: options.body ? '[REDACTED]' : undefined });

    try {
      const response = await fetch(url, {
        credentials: 'include',
        ...options,
        headers,
        // Add mode to handle CORS and SSL issues
        mode: 'cors'
      });

      console.log('Response status:', response.status, response.statusText);

      if (!response.ok) {
        let errorData: any;
        try {
          const responseText = await response.text();
          if (responseText) {
            try {
              errorData = JSON.parse(responseText);
            } catch (jsonParseError) {
              errorData = {
                detail: responseText.length > 200 ? responseText.substring(0, 200) + '...' : responseText,
                raw_response: responseText
              };
            }
          } else {
            errorData = { detail: `HTTP ${response.status}: ${response.statusText}` };
          }
        } catch (parseError) {
          errorData = {
            detail: `HTTP ${response.status}: ${response.statusText}`,
            parse_error: parseError instanceof Error ? parseError.message : 'Unknown parse error'
          };
        }

        const errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`;

        // Handle token expiration specifically
        if (response.status === 401 && (errorMessage.includes('expired') || errorMessage.includes('invalid') || errorMessage.includes('unauthorized'))) {
          console.log('Token has expired, clearing tokens');
          this.clearTokens();
          const err: any = new Error('Token has expired');
          err.status = 401;
          err.isTokenExpired = true;
          err.data = errorData;
          throw err;
        }

        // Only log detailed API errors in development
        if (process.env.NODE_ENV === 'development') {
          console.error('API Error:', errorData);
        } else {
          console.error('API Error:', errorMessage);
        }
        const err: any = new Error(errorMessage);
        err.status = response.status;
        err.data = errorData;
        throw err;
      }

      const data = await response.json();
      console.log('Response data:', data);
      return data;
    } catch (fetchError: any) {
      // Handle network errors, SSL issues, etc.
      console.error('Request failed:', fetchError);

      if (fetchError.name === 'TypeError' && fetchError.message.includes('fetch')) {
        // Network error or SSL certificate issue
        const err: any = new Error('Network error: Unable to connect to the server. This may be due to SSL certificate issues. Please check your connection and try again.');
        err.status = 0;
        err.isNetworkError = true;
        err.isSSLError = fetchError.message.includes('certificate') || fetchError.message.includes('SSL');
        throw err;
      }

      // Handle SSL certificate errors specifically
      if (fetchError.message && fetchError.message.includes('certificate')) {
        const err: any = new Error('SSL Certificate Error: The server is using a self-signed certificate. Please accept the certificate in your browser or contact your administrator.');
        err.status = 0;
        err.isSSLError = true;
        throw err;
      }

      // Re-throw other errors
      throw fetchError;
    }
  }

  async register(userData: RegistrationRequest): Promise<AuthResponse> {
    try {
      const response = await this.request<{ success: boolean; message: string; user_id: string; username: string; email: string; roles: string[]; status: string; }>('/auth/register', { method: 'POST', body: JSON.stringify(userData) });
      if (response.success) return { success: true, user: { id: response.user_id, username: response.username, email: response.email, first_name: userData.first_name, last_name: userData.last_name, clearance_level: 'unclassified', status: 'active', created_at: new Date().toISOString(), updated_at: new Date().toISOString() } };
      return { success: false, error: (response as any).message || 'Registration failed' };
    } catch (error) { return { success: false, error: error instanceof Error ? error.message : 'Registration failed' }; }
  }

  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await this.request<{ success: boolean; requires_mfa: boolean; mfa_methods?: string[]; access_token?: string; refresh_token?: string; user_id?: string; message?: string; }>('/auth/login', { method: 'POST', body: JSON.stringify(credentials) });
      if (response.success && response.access_token) {
        this.setTokens({ access_token: response.access_token, refresh_token: response.refresh_token || '' });
        let user: User | undefined;
        if (response.user_id) {
          const currentUser = await this.getCurrentUser();
          user = currentUser || undefined;
        }
        return {
          success: true,
          user,
          tokens: { access_token: response.access_token, refresh_token: response.refresh_token || '' },
          requiresMfa: response.requires_mfa,
          mfaMethods: response.mfa_methods?.map(method => ({
            id: method,
            type: 'totp' as const,
            name: method,
            isEnabled: true
          }))
        };
      } else if (response.success && response.requires_mfa) {
        return {
          success: true,
          requiresMfa: true,
          mfaMethods: response.mfa_methods?.map(method => ({
            id: method,
            type: 'totp' as const,
            name: method,
            isEnabled: true
          }))
        };
      }
      return { success: false, error: 'Authentication failed' };
    } catch (error: any) {
      if (error.status === 401) return { success: false, error: 'Invalid credentials' };
      if (error.status === 423) return { success: false, error: 'Account is locked due to multiple failed attempts', accountLocked: true };
      if (error.status === 400 && error.message?.includes('password expired')) return { success: false, error: 'Password has expired. Please reset your password.', passwordExpired: true };
      return { success: false, error: error?.message || 'Login failed' };
    }
  }

  async verifyMFA(mfaData: MFAVerificationRequest): Promise<AuthResponse> { try { const response = await this.request<AuthResponse>('/auth/mfa/verify', { method: 'POST', body: JSON.stringify(mfaData) }); if (response.success && response.tokens) this.setTokens(response.tokens); return response; } catch (error) { return { success: false, error: error instanceof Error ? error.message : 'MFA verification failed' }; } }

  async refreshTokens(): Promise<AuthResponse> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        return { success: false, error: 'No refresh token available' };
      }

      const response = await this.request<{ access_token?: string; refresh_token?: string; user_id?: string; token_type?: string; }>('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refreshToken })
      });

      if (response.access_token) {
        this.setTokens({ access_token: response.access_token, refresh_token: response.refresh_token || '' });
        return { success: true, requiresMfa: false, user: undefined, mfaMethods: [] };
      } else {
        return { success: false, error: 'Invalid response from server' };
      }
    } catch (error: any) {
      console.error('Token refresh error:', error);
      this.clearTokens();

      if (error.isSSLError) {
        return { success: false, error: 'SSL Certificate Error: The server is using a self-signed certificate. Please accept the certificate in your browser or contact your administrator.' };
      }

      if (error.isNetworkError) {
        return { success: false, error: 'Network error: Unable to connect to authentication server. Please check your connection and try again.' };
      }

      if (error.status === 401) {
        return { success: false, error: 'Session expired. Please log in again.' };
      }

      if (error.status === 500) {
        return { success: false, error: 'Server error during token refresh. Please try again.' };
      }

      return { success: false, error: error instanceof Error ? error.message : 'Token refresh failed' };
    }
  }

  /**
   * Logout user with proper cleanup and server notification
   */
  async logout(): Promise<{ success: boolean; error?: string }> {
    try {
      // Notify server about logout (for session cleanup, audit logs, etc.)
      try {
        await this.request('/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.getAccessToken()}`,
            'Content-Type': 'application/json'
          }
        });
      } catch (serverError) {
        // Server logout failed, but we'll still clean up locally
        console.warn('Server logout failed, proceeding with local cleanup:', serverError);
      }

      // Clear all authentication data
      this.clearTokens();

      // Clear any cached user data
      if (typeof window !== 'undefined') {
        // Clear session storage
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('permissions');
        sessionStorage.removeItem('preferences');

        // Clear any other auth-related local storage
        localStorage.removeItem('remember_me');
        localStorage.removeItem('last_login');

        // Clear any cookies (if using httpOnly cookies)
        document.cookie.split(";").forEach(cookie => {
          const eqPos = cookie.indexOf("=");
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          if (name.startsWith('auth_') || name.startsWith('session_')) {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          }
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);

      // Even if logout fails, clear tokens locally
      this.clearTokens();

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Logout failed'
      };
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await this.request<{
        id: string;
        username: string;
        email: string;
        first_name: string;
        last_name: string;
        employee_id?: string;
        job_title?: string;
        department?: string;
        manager_id?: string;
        clearance_level?: string;
        need_to_know_compartments?: string[];
        compliance_certifications?: string[];
        compliance_frameworks?: string[];
        employment_start_date?: string;
        last_training_date?: string;
        typical_access_hours?: number[][];
        status: string;
        mfa_enabled?: boolean;
        mfa_methods?: string[];
        last_login_at?: string;
        created_at: string;
        updated_at: string;
      }>('/auth/profile');

      return {
        id: response.id,
        username: response.username,
        email: response.email,
        first_name: response.first_name,
        last_name: response.last_name,
        employee_id: response.employee_id,
        job_title: response.job_title,
        department: response.department,
        manager_id: response.manager_id,
        clearance_level: response.clearance_level as 'unclassified' | 'confidential' | 'secret' | 'top_secret' | undefined,
        need_to_know_compartments: response.need_to_know_compartments,
        compliance_certifications: response.compliance_certifications,
        compliance_frameworks: response.compliance_frameworks,
        employment_start_date: response.employment_start_date,
        last_training_date: response.last_training_date,
        typical_access_hours: response.typical_access_hours,
        status: response.status as 'active' | 'inactive' | 'suspended',
        created_at: response.created_at,
        updated_at: response.updated_at,
        last_login_at: response.last_login_at,
        mfa_enabled: response.mfa_enabled || false,
        mfa_methods: response.mfa_methods || []
      };
    } catch (error: any) {
      console.log('getCurrentUser failed:', error?.message || error);

      // Handle specific error cases
      if (error?.status === 401 || error?.status === 403) {
        console.log('User not authenticated');
        return null;
      }

      // Handle network/connection errors gracefully
      if (error?.isNetworkError || error?.isSSLError) {
        console.log('Network/SSL error getting current user');
        return null;
      }

      console.log('Unexpected error getting current user');
      return null;
    }
  }

  async validateToken(): Promise<boolean> {
    try {
      const user = await this.getCurrentUser();
      return !!user;
    } catch (error: any) {
      // If token is expired, try to refresh it
      if (error?.isTokenExpired || error?.status === 401) {
        console.log('Token expired during validation, attempting refresh...');
        const refreshResult = await this.refreshTokens();
        if (refreshResult.success) {
          console.log('Token refresh successful during validation');
          return true;
        } else {
          console.log('Token refresh failed during validation');
          return false;
        }
      }
      return false;
    }
  }

  async initializeAuth(): Promise<boolean> {
    try {
      console.log('Initializing auth...');

      if (this.isAuthenticated()) {
        console.log('Token exists, validating...');
        const isValid = await this.validateToken();
        if (isValid) {
          console.log('Token is valid');
          return true;
        }
        console.log('Token is invalid, will try refresh');
      }

      console.log('Attempting token refresh...');
      const refreshResult = await this.refreshTokens();
      if (refreshResult.success) {
        console.log('Token refresh successful');
        return true;
      } else {
        console.log('Token refresh failed:', refreshResult.error);
        this.clearTokens();
        return false;
      }
    } catch (error: any) {
      console.log('Auth initialization error:', error?.message || error);

      // Handle specific error types
      if (error?.isTokenExpired || error?.status === 401) {
        console.log('Token expired during initialization, clearing tokens');
        this.clearTokens();
        return false;
      }

      // Don't spam console with network errors during development
      if (error?.isNetworkError || error?.isSSLError) {
        console.log('Network/SSL error during auth initialization - this is expected in development');
      }

      this.clearTokens();
      return false;
    }
  }

  private setTokens(tokens: AuthTokens): void {
    this.accessToken = tokens.access_token;
    this.refreshToken = tokens.refresh_token;
    if (typeof window !== 'undefined') {
      localStorage.setItem('access_token', tokens.access_token);
      localStorage.setItem('refresh_token', tokens.refresh_token);
    }
  }

  private clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  }

  getAccessToken(): string | null { return this.accessToken; }
  getRefreshToken(): string | null { return this.refreshToken; }
  isAuthenticated(): boolean {
    if (this.accessToken) return true;
    if (typeof window !== 'undefined') {
      const storedToken = localStorage.getItem('access_token');
      if (storedToken) {
        this.accessToken = storedToken;
        return true;
      }
    }
    return false;
  }

  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.request<{
        status: string;
        service: string;
        security_model: string;
        compliance_frameworks: string[];
        access_control: string;
        token_format: string;
        version: string;
      }>('/auth/health');
      return response.status === 'healthy';
    } catch {
      return false;
    }
  }

  async testApiConnection(): Promise<{ success: boolean; error?: string; details?: any }> {
    try {
      const response = await this.request<{
        status: string;
        service: string;
        security_model: string;
        compliance_frameworks: string[];
        access_control: string;
        token_format: string;
        version: string;
        timestamp: string;
      }>('/auth/health');
      return {
        success: true,
        details: response
      };
    } catch (error: any) {
      console.error('API connection test failed:', error);

      if (error.isSSLError) {
        return {
          success: false,
          error: 'SSL Certificate Error: The server is using a self-signed certificate. Please accept the certificate in your browser or contact your administrator.',
          details: {
            type: 'ssl_error',
            message: 'Self-signed certificate detected',
            solution: 'Accept the certificate in your browser by visiting https://192.168.56.130 directly and accepting the security warning'
          }
        };
      }

      if (error.isNetworkError) {
        return {
          success: false,
          error: 'Network Error: Unable to connect to the API server. Please check your connection and try again.',
          details: {
            type: 'network_error',
            message: 'Connection failed',
            solution: 'Check if the backend server is running and accessible'
          }
        };
      }

      return {
        success: false,
        error: error.message || 'Unknown error occurred',
        details: {
          type: 'unknown_error',
          message: error.message,
          status: error.status
        }
      };
    }
  }

  async getPublicKey(): Promise<string | null> { try { const response = await this.request<{ public_key: string }>('/auth/public-key'); return response.public_key; } catch { return null; } }
  async getSecurityRiskAssessment(): Promise<SecurityRiskAssessment | null> { try { return await this.request<SecurityRiskAssessment>('/auth/security/risk-assessment'); } catch { return null; } }
  async initiatePasswordRecovery(email: string): Promise<{ success: boolean; message: string }> { try { return await this.request<{ success: boolean; message: string }>('/auth/password-recovery/initiate', { method: 'POST', body: JSON.stringify({ email }) }); } catch { return { success: false, message: 'Password recovery failed' }; } }
  async verifyPasswordRecoveryToken(email: string, token: string): Promise<{ success: boolean; valid: boolean; message: string }> { try { return await this.request<{ success: boolean; valid: boolean; message: string }>('/auth/password-recovery/verify', { method: 'POST', body: JSON.stringify({ email, token }) }); } catch { return { success: false, valid: false, message: 'Token verification failed' }; } }
  async resetPasswordWithToken(email: string, token: string, newPassword: string): Promise<{ success: boolean; message: string }> { try { return await this.request<{ success: boolean; message: string }>('/auth/password-recovery/reset', { method: 'POST', body: JSON.stringify({ email, token, new_password: newPassword }) }); } catch { return { success: false, message: 'Password reset failed' }; } }
  async checkReauthenticationRequired(operation: string, password: string): Promise<ReauthenticationCheckResponse | null> { try { return await this.request<ReauthenticationCheckResponse>('/auth/reauthentication/check', { method: 'POST', body: JSON.stringify({ operation, password }) }); } catch { return null; } }
  async setupMFA(): Promise<{ totp_secret: string; backup_codes: string[]; qr_code_url: string } | null> { try { return await this.request<{ totp_secret: string; backup_codes: string[]; qr_code_url: string }>('/auth/mfa/setup'); } catch { return null; } }
  async getMFAStatus(): Promise<{ enabled: boolean; methods: string[] } | null> { try { const user = await this.getCurrentUser(); if (user) return { enabled: user.mfa_enabled || false, methods: user.mfa_methods || [] }; return null; } catch { return null; } }
  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> { try { const response = await this.request<{ message: string }>('/auth/change-password', { method: 'POST', body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }) }); return { success: true, message: response.message }; } catch { return { success: false, message: 'Password change failed' }; } }
  async getAccountSecurityStatus(): Promise<{ isLocked: boolean; passwordExpired: boolean; mfaEnabled: boolean; riskLevel: 'low' | 'medium' | 'high'; lastLoginAt?: string; } | null> { try { const user = await this.getCurrentUser(); const riskAssessment = await this.getSecurityRiskAssessment(); if (user) return { isLocked: user.status === 'suspended', passwordExpired: false, mfaEnabled: user.mfa_enabled || false, riskLevel: (riskAssessment?.riskLevel || 'low'), lastLoginAt: user.last_login_at }; return null; } catch { return null; } }

}
export const authService = new AuthService();

